import type { SiteContent } from "../schema.ts";
import { media } from "../media.ts";

const p = media.projects;

export const en = {
  locale: "en",
  dir: "ltr",
  localeSwitch: {
    href: "/ar",
    label: "العربية",
    aria: "Switch to Arabic",
  },
  name: "Xattir",
  slogan: "Products that ship. Experiences that stick.",
  title: "Xattir. Design and engineering for ambitious teams",
  description:
    "Xattir designs and builds high-impact web and mobile products for education, healthcare, commerce, and enterprise teams.",
  origin: media.origin,
  ogImage: media.ogImage,
  ogImageAlt: "Xattir. Design and engineering.",
  keywords: [
    "Xattir",
    "product design",
    "product engineering",
    "web apps",
    "mobile apps",
    "SaaS",
  ],
  nav: [
    { label: "Work", section: "work" },
    { label: "Services", section: "services" },
    { label: "Products", section: "products" },
    { label: "Our Story", section: "story" },
  ],
  jumpMenu: [
    { label: "Home", section: "hero" },
    { label: "Our Work", section: "work" },
    { label: "Our Services", section: "services" },
    { label: "Our Products", section: "products" },
    { label: "Our Story", section: "story" },
    { label: "Contact us", section: "contact" },
  ],
  cta: { label: "Start a project", section: "contact" },
  hero: {
    lines: ["Design & Engineering", "for Real Outcomes."],
    subhead:
      "We design, engineer, and ship digital products that ambitious teams put in front of real users.",
  },
  work: {
    lines: ["Selected Work.", "Real Products.", "Shipped Fast."],
    label: "Latest\nprojects",
    more: "And more.",
  },
  projects: [
    {
      id: "case-1",
      title: "MKIT",
      client: "MKIT",
      description:
        "We designed and engineered MKIT, a senior-kit commerce experience where schools customize every detail, from embroidery to nicknames, and seniors order in one seamless flow.",
      tags: [
        "Product Design",
        "E-commerce",
        "Platform Engineering",
        "Brand Experience",
      ],
      outcome:
        "A school-ready kit builder with secure checkout and memorable brand presence.",
      url: p["case-1"].url,
      cover: p["case-1"].cover,
      coverMobile: p["case-1"].coverMobile,
      coverAlt: "Abstract studio photograph of tailored apparel hardware.",
      slide: p["case-1"].slide,
      caseStudyId: "mkit",
    },
    {
      id: "case-2",
      title: "Kenaz Advisors",
      client: "Kenaz",
      description:
        "We built Kenaz as an ACCA learning and advisory platform. Live and recorded courses, advisor profiles, enrollments, and content that helps finance professionals clear exams and grow.",
      tags: [
        "EdTech Platform",
        "UI/UX Design",
        "Course Commerce",
        "Content Systems",
      ],
      outcome:
        "A trusted ACCA learning hub with courses, advisors, and podcast-led engagement.",
      url: p["case-2"].url,
      cover: p["case-2"].cover,
      coverMobile: p["case-2"].coverMobile,
      coverAlt: "Warm still life of notebooks and a desk lamp.",
      slide: p["case-2"].slide,
      caseStudyId: "kenaz",
    },
    {
      id: "case-3",
      title: "CareLend",
      client: "CareLend",
      description:
        "We delivered CareLend’s international nursing careers platform, connecting qualified nurses with German hospitals through fair recruitment, language training, and end-to-end placement journeys.",
      tags: [
        "Healthcare Platform",
        "Multi-Market UX",
        "Recruitment Journeys",
        "Full-Stack Delivery",
      ],
      outcome:
        "An ethical placement experience spanning eligibility, training, visa, and integration.",
      url: p["case-3"].url,
      cover: p["case-3"].cover,
      coverMobile: p["case-3"].coverMobile,
      coverAlt: "Quiet contemporary corridor of stone and daylight.",
      slide: p["case-3"].slide,
      caseStudyId: "carelend",
    },
    {
      id: "case-4",
      title: "And more.",
      client: "Xattir",
      description:
        "A wider look at products we design and ship: education, housing, healthcare engineering, and vocational training.",
      tags: ["Platform Engineering", "Product Design", "Multi-Market Delivery"],
      outcome:
        "Selected screens from Torque Academy, CL-Bildung, SFNDY, Qimmmah, and more.",
      url: p["case-4"].url,
      cover: p["case-4"].cover,
      coverMobile: p["case-4"].coverMobile,
      coverAlt: "Abstract collage of frosted glass panels.",
      plane: p["case-4"].plane,
      slide: p["case-4"].slide,
      marquee: true,
    },
  ],
  projectsMore: media.projectsMore,
  caseStudies: [
    {
      id: "mkit",
      title: "MKIT",
      client: "MKIT",
      eyebrow: "Commerce platform",
      summary:
        "A senior-kit builder where schools customize every detail and graduates order in one flow.",
      problem:
        "Schools needed branded kit commerce without spreadsheets, broken checkouts, or one-off production chaos.",
      outcome:
        "A school-ready kit platform with embroidery controls, secure checkout, and a brand presence seniors remember.",
      stack: ["Next.js", "Commerce", "Custom configurator", "Payments"],
      preview: media.caseStudies.mkit,
      previewAlt: "MKIT product interface preview.",
      url: "https://new.mkiteg.com/",
    },
    {
      id: "kenaz",
      title: "Kenaz Advisors",
      client: "Kenaz",
      eyebrow: "EdTech + advisory",
      summary:
        "An ACCA learning hub with live courses, advisors, enrollments, and content that converts ambition into progress.",
      problem:
        "Finance professionals needed one trusted place for courses, advisors, and exam-ready content.",
      outcome:
        "A learning and advisory platform that pairs course commerce with podcast-led engagement.",
      stack: ["Product design", "Course commerce", "CMS", "Enrollment flows"],
      preview: media.caseStudies.kenaz,
      previewAlt: "Kenaz Advisors platform preview.",
      url: "https://kenazadvisors.com/",
    },
    {
      id: "carelend",
      title: "CareLend",
      client: "CareLend",
      eyebrow: "Healthcare careers",
      summary:
        "Ethical nursing placement from eligibility through language training, visa, and hospital integration.",
      problem:
        "Cross-border nursing recruitment was fragmented across agencies, paperwork, and opaque candidate journeys.",
      outcome:
        "A multi-market platform that keeps nurses and hospitals aligned from first step to placement.",
      stack: ["Full-stack", "Multi-locale UX", "Workflow systems", "CRM"],
      preview: media.caseStudies.carelend,
      previewAlt: "CareLend careers platform preview.",
      url: "https://carelend.xattir.com/en",
    },
    {
      id: "torque",
      title: "Torque Academy",
      client: "Torque Academy",
      eyebrow: "Learning product",
      summary:
        "A vocational learning surface built for clarity, progression, and real delivery under load.",
      problem:
        "Training teams needed a product that felt premium without slowing course ops or learner momentum.",
      outcome:
        "A focused academy experience with crisp UI and paths learners can actually finish.",
      stack: ["Product design", "Learning flows", "Web app"],
      preview: media.caseStudies.torque,
      previewAlt: "Torque Academy interface preview.",
      url: "#",
    },
    {
      id: "cl-bildung",
      title: "CL-Bildung",
      client: "CL-Bildung",
      eyebrow: "Education systems",
      summary:
        "Education product engineering for structured programs and multi-market delivery.",
      problem:
        "Program operators needed a digital system that matched the rigor of in-person vocational training.",
      outcome:
        "A clean education surface that keeps programs, learners, and operations in one rhythm.",
      stack: ["Platform engineering", "Design systems", "Integrations"],
      preview: media.caseStudies["cl-bildung"],
      previewAlt: "CL-Bildung product preview.",
      url: "#",
    },
    {
      id: "sfndy",
      title: "SFNDY",
      client: "SFNDY",
      eyebrow: "Housing + ops",
      summary:
        "Product craft for housing and operations teams that need speed without losing trust.",
      problem:
        "Operators needed software that felt as reliable as the service they sell.",
      outcome:
        "A composed product experience tuned for day-to-day ops and customer-facing clarity.",
      stack: ["Product design", "Web app", "Ops workflows"],
      preview: media.caseStudies.sfndy,
      previewAlt: "SFNDY product preview.",
      url: "#",
    },
    {
      id: "qimmmah",
      title: "Qimmmah",
      client: "Qimmmah",
      eyebrow: "Tech + brand",
      summary:
        "A sharp digital product pairing technical depth with a brand teams are proud to show.",
      problem:
        "The product story and the engineering needed to land as one coherent experience.",
      outcome:
        "A polished surface that carries both capability and identity without noise.",
      stack: ["UI/UX", "Front-end", "Brand systems"],
      preview: media.caseStudies.qimmmah,
      previewAlt: "Qimmmah product preview.",
      url: "#",
    },
  ],
  services: [
    {
      id: "digital",
      name: "Digital Experiences",
      description:
        "Websites, landing pages, and commerce journeys built to convert. Clear, fast, and on-brand.",
      subservices: [
        "Websites",
        "Landing Pages",
        "Interactive Experiences",
        "E-commerce",
        "Content Systems",
      ],
      highlights: [
        "Marketing sites and landings engineered for conversion, not decoration",
        "Commerce journeys with clear paths from browse to checkout",
        "Motion and interaction that guide attention without slowing the page",
        "CMS-ready content models editors can actually ship with",
        "Performance budgets and accessibility built into the craft",
      ],
      image: media.services.digital,
      imageAlt: "Dark editorial still of a frosted digital surface.",
    },
    {
      id: "product",
      name: "Product Development",
      description:
        "SaaS, web apps, and platforms engineered to ship fast and stay stable under real load.",
      subservices: [
        "SaaS Products",
        "Web Applications",
        "Dashboards",
        "Platforms",
        "Custom Software",
      ],
      highlights: [
        "SaaS and web apps with architecture that survives real users",
        "Dashboards that make dense data feel obvious",
        "Platform foundations you can extend without a rewrite",
        "Custom software scoped to outcomes, not feature theater",
        "Ship cycles with review, QA, and release discipline",
      ],
      image: media.services.product,
      imageAlt: "Layered dark acrylic blocks suggesting product architecture.",
    },
    {
      id: "mobile",
      name: "Mobile Apps",
      description:
        "iOS, Android, and cross-platform apps that feel native, perform well, and stay maintainable.",
      subservices: [
        "iOS Apps",
        "Android Apps",
        "Cross-Platform Apps",
        "App Store Delivery",
        "Mobile UX",
      ],
      highlights: [
        "Native-feeling iOS and Android experiences",
        "Cross-platform builds when speed and parity matter",
        "Mobile UX tuned for thumbs, offline edges, and real devices",
        "Store submission, versioning, and release hygiene",
        "Maintainable codebases your team can keep shipping",
      ],
      image: media.services.mobile,
      imageAlt: "Matte black phone edge with soft rim light.",
    },
    {
      id: "design",
      name: "Product Design",
      description:
        "UX, UI, and design systems that make complex products clear, from strategy to polished interface.",
      subservices: [
        "UX Research",
        "UI Design",
        "Design Systems",
        "Product Strategy",
        "Prototyping",
      ],
      highlights: [
        "Research that surfaces real jobs-to-be-done",
        "Interfaces with hierarchy, restraint, and brand gravity",
        "Design systems that scale without visual drift",
        "Strategy workshops that lock the problem before the pixels",
        "Prototypes that de-risk the expensive decisions early",
      ],
      image: media.services.design,
      imageAlt: "Black desk with geometric grid and paper cards.",
    },
    {
      id: "ai",
      name: "AI & Automation",
      description:
        "AI products, agents, and intelligent workflows that remove busywork and unlock new capabilities.",
      subservices: [
        "AI Products",
        "Agents",
        "Intelligent Workflows",
        "Business Automation",
        "LLM Integration",
      ],
      highlights: [
        "AI features wired into product flows, not bolted on as demos",
        "Agents and workflows that cut busywork with measurable ROI",
        "LLM integration with guardrails, evals, and human override",
        "Automation across the tools your teams already run",
        "Responsible defaults: privacy, cost control, and fallbacks",
      ],
      image: media.services.ai,
      imageAlt: "Dark constellation of connected workflow nodes.",
    },
    {
      id: "systems",
      name: "Systems & Integrations",
      description:
        "APIs and integrations that connect payments, CRMs, ERPs, and the tools your teams already run.",
      subservices: [
        "APIs",
        "Third-Party Integrations",
        "Payments",
        "CRM & ERP",
        "Internal Tools",
      ],
      highlights: [
        "APIs designed for partners and your own product surface",
        "Payment, CRM, and ERP connections that stay reliable",
        "Third-party glue without fragile one-off scripts",
        "Internal tools that replace spreadsheet workarounds",
        "Observability so integrations fail loudly and recover cleanly",
      ],
      image: media.services.systems,
      imageAlt: "Machined metal connectors on charcoal.",
    },
    {
      id: "scale",
      name: "Scale & Support",
      description:
        "Cloud, DevOps, and ongoing development so what you ship keeps getting faster and more reliable.",
      subservices: [
        "Cloud Infrastructure",
        "DevOps",
        "Performance Optimization",
        "Modernization",
        "Ongoing Development",
      ],
      highlights: [
        "Cloud infrastructure sized for growth, not guesswork",
        "DevOps pipelines that make releases boring in the best way",
        "Performance work that users feel on first paint",
        "Modernization paths off legacy without a big-bang rewrite",
        "Ongoing development retainers that protect momentum",
      ],
      image: media.services.scale,
      imageAlt: "Quiet server-hall aisle in near darkness.",
    },
  ],
  pages: {
    services: {
      title: "Services",
      description:
        "Design, engineering, AI, and systems delivery from Xattir. One craft team across experiences, products, and scale.",
      headline: "What we ship.",
      subhead:
        "Seven service lines. One standard: products and experiences ambitious teams can put in front of real users.",
      band: {
        headline: "Ready to build?",
        body: "Tell us the outcome. We’ll map the service mix, the stack, and the first ship date.",
        cta: "Start a project",
      },
    },
    work: {
      title: "Work",
      description:
        "Selected Xattir case studies across commerce, education, healthcare, and product platforms.",
      headline: "Selected work.",
      subhead:
        "Case studies from products we designed and engineered. Open a card for the problem, outcome, and stack.",
      band: {
        headline: "Have a product in mind?",
        body: "We partner with teams who need craft and delivery in the same room. Let’s talk scope.",
        cta: "Start a project",
      },
    },
  },
  products: [
    {
      id: "productA",
      name: "Atlas",
      tagline: "Built to learn",
      description:
        "A learning platform that turns courses, groups, and assessments into clear journeys, so instructors teach with focus and learners always know what comes next.",
      sideDescription:
        "Courses, groups, and assessments in one learning platform. Clear paths for instructors and learners.",
      modules: [
        "Course Paths",
        "Live & group sessions",
        "Assessments & Certs",
        "Progress Insights",
      ],
      integrations: [
        "SSO & identity",
        "Payment gateways",
        "Video platforms",
        "Analytics",
      ],
      integrationsLabel: "Integration notes",
      modulesLabel: "Core modules",
      video: media.products.productA.video,
      logos: media.products.productA.logos,
    },
    {
      id: "productB",
      name: "Orbit",
      tagline: "Relationships in rhythm",
      description:
        "A CRM that keeps pipeline, conversations, and handoffs in one place, so sales and success teams always know the next move.",
      sideDescription:
        "Pipeline, conversations, and handoffs in one CRM, so sales and success always know the next move.",
      modules: [
        "Pipeline & Deals",
        "Contacts & Accounts",
        "Activity Timeline",
        "Insights & Alerts",
      ],
      integrations: [
        "Email & calendar",
        "Payment gateways",
        "Third-party systems",
        "Analytics",
      ],
      integrationsLabel: "Integration notes",
      modulesLabel: "Core modules",
      video: media.products.productB.video,
      logos: media.products.productB.logos,
    },
  ],
  about: {
    headline: ["Without borders.", "Built to last."],
    body: "Xattir exists to ship products that create real transactions and experiences people remember. We balance craft, systems thinking, and delivery so digital products work at scale, and stay in the hearts and minds of users.",
    reach: "We build for ambitious teams wherever they are.",
    facts: [
      { value: 80, suffix: "+", label: "Projects" },
      { value: null, suffix: "", label: "Markets", symbol: "∞" },
      { value: 5, suffix: "+", label: "Countries" },
      { value: 10, suffix: "+", label: "Services" },
    ],
  },
  clients: [
    { name: "MKIT" },
    { name: "Kenaz Advisors" },
    { name: "CareLend" },
    { name: "Torque Academy" },
    { name: "CL-Bildung" },
    { name: "SFNDY" },
    { name: "Qimmmah" },
  ],
  markets: [
    { id: "eg", name: "Egypt", ...media.markets.eg },
    { id: "kw", name: "Kuwait", ...media.markets.kw },
    { id: "ae", name: "UAE", ...media.markets.ae },
    { id: "sa", name: "KSA", ...media.markets.sa },
    { id: "us", name: "USA", ...media.markets.us },
    { id: "de", name: "Germany", ...media.markets.de },
    { id: "dk", name: "Denmark", ...media.markets.dk },
  ],
  contact: {
    successTitle: "Thank you for reaching out!",
    successMessage: "Our team will get back to you as soon as possible.",
    headline: "Let’s build what’s next.",
    submit: "Send message",
    cover: media.contactCover,
    currencies: ["EGP", "USD"],
    prompts: [
      "What are you building?",
      "What problem are you trying to solve?",
      "What would success look like?",
      "Tell us about your idea.",
      "What are your goals?",
    ],
    form: {
      nameLabel: "Name",
      namePlaceholder: "Full name",
      businessLabel: "Business name",
      businessOptional: "(optional)",
      businessPlaceholder: "Company or studio",
      phoneLabel: "Phone number",
      phonePlaceholder: "1XX XXX XXXX",
      emailLabel: "Email",
      emailOptional: "(optional)",
      emailPlaceholder: "Business email",
      budgetLabel: "Expected investment range",
      budgetPlaceholder: "Preferred range",
      currencyPlaceholder: "Currency",
      detailsLabel: "Describe your project and goals",
      detailsPlaceholder: "Tell us more.",
    },
    errors: {
      nameRequired: "Name is required",
      phoneRequired: "Phone is required",
      phoneCountry: "Choose a country code",
      phoneInvalid: "Enter a valid phone number for the selected country",
      emailInvalid: "Enter a valid email",
      currencyRequired: "Currency is required",
      detailsRequired: "Tell us a bit more about the project",
      submitFailed: "Something went wrong. Please try again.",
    },
    whatsappMessage: "Hi Xattir — I'd like to talk about a project.",
  },
  offices: media.offices,
  sideWork: "",
  sideContact:
    "Tell us what you’re building. We’ll help you ship it with clarity and craft.",
  sideContactFocus: [
    "Platform Engineering",
    "Product Design",
    "Multi-Market Delivery",
  ],
  labels: {
    contactUs: "Contact us",
    bookDemo: "Book a demo",
    subservices: "Sub-services",
    focus: "Focus",
    trustedBy: "Trusted by teams at",
    viewAllServices: "View all services",
    viewCaseStudy: "View case study",
    viewAllProjects: "View all projects",
    close: "Close",
    visitSite: "Visit site",
    problem: "Problem",
    outcome: "Outcome",
    stack: "Stack",
    backHome: "Back home",
  },
  aria: {
    home: "Xattir. Home",
    jumpMenu: "Page sections",
    marketsMap: "Markets we work across",
    storyVisual: "Night view of Earth as a field of lights",
    landscapeMessage: "For a better experience, rotate to portrait.",
    landscapeDismiss: "Dismiss",
    decorative: "Decorative",
    infinity: "Infinity",
    countryCodes: "Country calling codes",
  },
} as const satisfies SiteContent;
